import { useState } from "react";

import { errorText } from "../../api/client";
import { searchCompanies } from "../../api/companies";
import type { CompanyListItem } from "../../api/companies";
import { Button } from "../../components/Button";
import { Combobox } from "../../components/Combobox";
import { Dialog } from "../../components/Dialog";
import { FormError } from "../../components/Field";
import { nl } from "../../i18n/nl";
import styles from "./moderation.module.css";

const t = nl.admin.moderation;

/** Pick the company that stays; merging deletes the source, so it asks for confirmation. */
export function MergeDialog({
  source,
  open,
  onOpenChange,
  onConfirm,
  pending,
  error,
}: {
  source: { id: string; name: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (into: CompanyListItem) => void;
  pending: boolean;
  error: unknown;
}) {
  const [into, setInto] = useState<CompanyListItem | null>(null);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setInto(null);
        onOpenChange(next);
      }}
      title={t.mergeTitle(source.name)}
      description={t.mergeLead}
    >
      <div className={styles.dialogBody}>
        <Combobox<CompanyListItem>
          label={t.mergeSearch}
          value={into ? `${into.name} · ${into.municipality}` : null}
          queryKey="merge-target"
          search={async (term) =>
            (await searchCompanies(term))
              .filter((c) => c.id !== source.id)
              .map((c) => ({ key: c.id, primary: c.name, secondary: c.municipality, data: c }))
          }
          onSelect={(option) => {
            setInto(option.data);
          }}
          onClear={() => {
            setInto(null);
          }}
          clearLabel={t.cancel}
          noneText={t.mergeNone}
          searchingText={t.mergeSearching}
        />
        {into && <p className={styles.warningText}>{t.mergeExplain(source.name, into.name)}</p>}
        <FormError message={error ? errorText(error) : null} />
        <div className={styles.dialogActions}>
          <Button
            onClick={() => {
              setInto(null);
              onOpenChange(false);
            }}
          >
            {t.cancel}
          </Button>
          <Button
            variant="primary"
            disabled={!into || pending}
            onClick={() => {
              if (into) onConfirm(into);
            }}
          >
            {t.mergeConfirm}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
